import { useState } from 'react';
import { useLocation } from 'react-router-dom';

const C = {
  navy:     '#0C1A36',
  navy2:    '#112240',
  teal:     '#00A8CC',
  gold:     '#F5C800',
  offwhite: '#EDF1FF',
  muted:    '#7A90B8',
  green:    '#3DAA4A',
  red:      '#C0223A',
};

const inputStyle = (focused) => ({
  width:           '100%',
  padding:         '12px 16px',
  backgroundColor: C.navy,
  border:          `1px solid ${focused ? C.teal : '#2A3F5F'}`,
  borderRadius:    '8px',
  color:           C.offwhite,
  fontSize:        '1rem',
  fontFamily:      'Sora, sans-serif',
  boxSizing:       'border-box',
  outline:         'none',
  transition:      'border-color 0.15s',
});

const labelStyle = {
  display:      'block',
  color:        C.offwhite,
  fontSize:     '0.875rem',
  marginBottom: '8px',
  fontWeight:   600,
};

export default function ResetPassword() {
  const location = useLocation();
  const token    = new URLSearchParams(location.search).get('token');

  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading,         setLoading]         = useState(false);
  const [success,         setSuccess]         = useState(false);
  const [error,           setError]           = useState('');
  const [focusedNew,      setFocusedNew]      = useState(false);
  const [focusedConfirm,  setFocusedConfirm]  = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const res  = await fetch('/api/users/reset-password', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ token, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to reset password.');
        return;
      }
      setSuccess(true);
    } catch {
      setError('Network error — please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  const card = (
    <div style={{
      backgroundColor: C.navy2,
      borderRadius:    '12px',
      padding:         '48px',
      width:           '100%',
      maxWidth:        '420px',
      boxShadow:       '0 20px 60px rgba(0,0,0,0.4)',
    }}>
      {/* Wordmark */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <div style={{ fontSize: '1.8rem', fontWeight: 800, letterSpacing: '-0.5px' }}>
          <span style={{ color: '#1A7FBE' }}>Acct</span>
          <span style={{ color: C.gold  }}>4</span>
          <span style={{ color: C.green }}>Cast</span>
        </div>
        <p style={{ color: C.muted, fontSize: '0.875rem', marginTop: '8px', marginBottom: 0 }}>
          CFO-level financial intelligence
        </p>
      </div>

      {/* No token */}
      {!token ? (
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: C.red, fontSize: '0.95rem', marginBottom: '20px' }}>
            Invalid reset link. Please request a new one.
          </div>
          <a href="/forgot-password" style={{ color: C.teal, textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem' }}>
            Request a new link →
          </a>
        </div>
      ) : success ? (
        /* ── Success state ── */
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width:          '56px',
            height:         '56px',
            borderRadius:   '50%',
            background:     C.green + '22',
            border:         `2px solid ${C.green}`,
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            margin:         '0 auto 20px',
            fontSize:       '1.6rem',
            color:          C.green,
          }}>
            ✓
          </div>
          <h2 style={{ color: C.offwhite, fontSize: '1.25rem', fontWeight: 700, margin: '0 0 12px' }}>
            Password updated!
          </h2>
          <p style={{ color: C.muted, fontSize: '0.875rem', lineHeight: 1.6, margin: '0 0 28px' }}>
            You can now log in with your new password.
          </p>
          <a
            href="/login"
            style={{
              display:       'inline-block',
              background:    `linear-gradient(135deg, ${C.teal}, ${C.green})`,
              color:         '#fff',
              padding:       '12px 32px',
              borderRadius:  '8px',
              textDecoration:'none',
              fontWeight:    700,
              fontSize:      '0.95rem',
              fontFamily:    'Sora, sans-serif',
            }}
          >
            Go to Login
          </a>
        </div>
      ) : (
        /* ── Form state ── */
        <>
          <h2 style={{ color: C.offwhite, fontSize: '1.25rem', fontWeight: 700, margin: '0 0 8px', textAlign: 'center' }}>
            Set New Password
          </h2>
          <p style={{ color: C.muted, fontSize: '0.875rem', textAlign: 'center', margin: '0 0 28px', lineHeight: 1.5 }}>
            Enter your new password below.
          </p>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>New password</label>
              <input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                onFocus={() => setFocusedNew(true)}
                onBlur={() => setFocusedNew(false)}
                required
                placeholder="••••••••"
                style={inputStyle(focusedNew)}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Confirm new password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                onFocus={() => setFocusedConfirm(true)}
                onBlur={() => setFocusedConfirm(false)}
                required
                placeholder="••••••••"
                style={inputStyle(focusedConfirm)}
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <div style={{ color: C.red, fontSize: '0.8rem', marginTop: '6px' }}>
                  Passwords do not match.
                </div>
              )}
            </div>

            {error && (
              <div style={{
                background:   C.red + '18',
                border:       `1px solid ${C.red}`,
                borderRadius: '8px',
                color:        C.red,
                fontSize:     '0.875rem',
                padding:      '10px 14px',
                marginBottom: '16px',
              }}>
                {error}
                {error.toLowerCase().includes('expired') && (
                  <div style={{ marginTop: '8px' }}>
                    <a href="/forgot-password" style={{ color: C.teal, textDecoration: 'none', fontWeight: 600 }}>
                      Request a new link →
                    </a>
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width:         '100%',
                padding:       '14px',
                background:    `linear-gradient(135deg, ${C.teal}, ${C.green})`,
                border:        'none',
                borderRadius:  '8px',
                color:         '#fff',
                fontSize:      '1rem',
                fontFamily:    'Sora, sans-serif',
                fontWeight:    700,
                cursor:        loading ? 'not-allowed' : 'pointer',
                opacity:       loading ? 0.7 : 1,
                letterSpacing: '0.3px',
                marginBottom:  '20px',
              }}
            >
              {loading ? 'Updating…' : 'Update Password'}
            </button>
          </form>
        </>
      )}
    </div>
  );

  return (
    <div style={{
      minHeight:       '100vh',
      display:         'flex',
      alignItems:      'center',
      justifyContent:  'center',
      backgroundColor: C.navy,
      fontFamily:      'Sora, sans-serif',
      padding:         '16px',
    }}>
      {card}
    </div>
  );
}
